/**
 * Stock Management - UI แบบ foodorder เชื่อมระบบคลัง thunderbee
 * ใช้ getDataWithComp, insertData, updateData, deleteData (Tb_Set_WH)
 */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Input, Form, Space, Modal } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, SearchOutlined } from "@ant-design/icons";
import Sidebar from "../../components/Sidebar";
import Topbar from "../../components/Topbar";
import DataTable from "../../../components/DataTable";
import { ConfirmDialog, ErrorDialog } from "../../../components/Dialog";
import { getDataWithComp, getAlert, insertData, updateData, deleteData } from "../../../utils/SamuiUtils";

export default function StockManagementPage() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [recentlyModifiedId, setRecentlyModifiedId] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, record: null });
  const [searchText, setSearchText] = useState("");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [initialFormValues, setInitialFormValues] = useState(null);
  const [saveConfirmDialog, setSaveConfirmDialog] = useState(false);
  const [closeConfirmDialog, setCloseConfirmDialog] = useState(false);
  const [resultDialog, setResultDialog] = useState({ isOpen: false, type: "success", message: "" });

  const fetchWarehouses = async () => {
    setLoading(true);
    try {
      const rows = await getDataWithComp("Tb_Set_WH", "AND WH_Status = 'Y' ORDER BY WH_Name ASC");
      setWarehouses(Array.isArray(rows) ? rows : []);
    } catch (err) {
      getAlert("FAILED", err?.message || "โหลดรายการคลังไม่สำเร็จ");
      setWarehouses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWarehouses();
  }, []);

  const handleAdd = () => {
    setEditingWarehouse(null);
    const initValues = { WH_Code: "", WH_Name: "", WH_Remark: "" };
    form.resetFields();
    form.setFieldsValue(initValues);
    setInitialFormValues(initValues);
    setHasUnsavedChanges(false);
    setModalOpen(true);
  };

  const handleEdit = (record) => {
    setEditingWarehouse(record);
    const values = { WH_Code: record.WH_Code || "", WH_Name: record.WH_Name || "", WH_Remark: record.WH_Remark || "" };
    form.setFieldsValue(values);
    setInitialFormValues(values);
    setHasUnsavedChanges(false);
    setModalOpen(true);
  };

  const handleFormChange = () => {
    const currentValues = form.getFieldsValue();
    setHasUnsavedChanges(JSON.stringify(currentValues || {}) !== JSON.stringify(initialFormValues || {}));
  };

  const handleModalOk = () => setSaveConfirmDialog(true);

  const executeSave = async () => {
    setSaveConfirmDialog(false);
    try {
      const values = await form.validateFields();
      if (!values.WH_Status) values.WH_Status = "Y";

      const duplicateCode = warehouses.find(
        (w) =>
          w.WH_Code &&
          w.WH_Code.trim().toLowerCase() === values.WH_Code.trim().toLowerCase() &&
          (!editingWarehouse || w.WH_Id !== editingWarehouse.WH_Id)
      );
      if (duplicateCode) {
        form.setFields([{ name: "WH_Code", errors: [`รหัสคลัง "${values.WH_Code}" มีอยู่แล้วในระบบ`] }]);
        return;
      }

      const duplicateName = warehouses.find(
        (w) =>
          w.WH_Name &&
          w.WH_Name.trim().toLowerCase() === values.WH_Name.trim().toLowerCase() &&
          (!editingWarehouse || w.WH_Id !== editingWarehouse.WH_Id)
      );
      if (duplicateName) {
        form.setFields([{ name: "WH_Name", errors: [`ชื่อคลัง "${values.WH_Name}" มีอยู่แล้วในระบบ`] }]);
        return;
      }

      setLoading(true);
      const compId = window.localStorage.getItem("company") || "";

      if (editingWarehouse) {
        const updateValues = {
          WH_Code: (values.WH_Code || "").trim(),
          WH_Name: (values.WH_Name || "").trim(),
          WH_Status: values.WH_Status === "N" ? "N" : "Y",
          WH_Remark: values.WH_Remark || "",
        };
        const res = await updateData("Tb_Set_WH", updateValues, { WH_Id: editingWarehouse.WH_Id });
        if (res?.status === "OK" || res?.status === undefined) {
          setRecentlyModifiedId(editingWarehouse.WH_Id);
          setTimeout(() => setRecentlyModifiedId(null), 3000);
          setResultDialog({ isOpen: true, type: "success", message: "แก้ไขคลังสำเร็จ" });
          await fetchWarehouses();
          setModalOpen(false);
          setHasUnsavedChanges(false);
          form.resetFields();
        } else {
          setResultDialog({ isOpen: true, type: "error", message: res?.message || "ไม่สามารถแก้ไขคลังได้" });
        }
      } else {
        const fields = "WH_Code, WH_Name, WH_Status, WH_Remark, Comp_Id, Flag_Vansale";
        const vals = [(values.WH_Code || "").trim(), (values.WH_Name || "").trim(), values.WH_Status === "N" ? "N" : "Y", values.WH_Remark || "", compId, "N"];
        const res = await insertData("Tb_Set_WH", fields, vals);
        if (res?.status === "OK" || (res?.status === undefined && res)) {
          const newList = await getDataWithComp("Tb_Set_WH", "ORDER BY WH_Id DESC");
          const created = Array.isArray(newList) && newList.length > 0 ? newList[0] : null;
          setRecentlyModifiedId(created?.WH_Id);
          setTimeout(() => setRecentlyModifiedId(null), 3000);
          setResultDialog({ isOpen: true, type: "success", message: "เพิ่มคลังสำเร็จ" });
          await fetchWarehouses();
          setModalOpen(false);
          setHasUnsavedChanges(false);
          form.resetFields();
        } else {
          setResultDialog({ isOpen: true, type: "error", message: res?.message || "ไม่สามารถเพิ่มคลังได้" });
        }
      }
    } catch (err) {
      setResultDialog({ isOpen: true, type: "error", message: err?.message || "เกิดข้อผิดพลาดในการบันทึกข้อมูล" });
    } finally {
      setLoading(false);
    }
  };

  const handleModalCancel = () => {
    if (hasUnsavedChanges) setCloseConfirmDialog(true);
    else {
      setModalOpen(false);
      form.resetFields();
      setHasUnsavedChanges(false);
    }
  };

  const executeClose = () => {
    setCloseConfirmDialog(false);
    setModalOpen(false);
    form.resetFields();
    setHasUnsavedChanges(false);
  };

  const handleDelete = (record) => setConfirmDialog({ isOpen: true, record });

  const executeDelete = async () => {
    const record = confirmDialog.record;
    if (!record?.WH_Id) {
      setConfirmDialog({ isOpen: false, record: null });
      return;
    }
    try {
      const compId = record.Comp_Id || window.localStorage.getItem("company") || "";
      const whereClause = compId ? `WHERE WH_Id = ${record.WH_Id} AND Comp_Id = '${compId}'` : `WHERE WH_Id = ${record.WH_Id}`;
      const res = await deleteData("Tb_Set_WH", whereClause);
      if (res?.status === "OK" || res?.status === undefined) {
        getAlert("SUCCESS", "ลบคลังสำเร็จ");
        await fetchWarehouses();
      } else {
        getAlert("FAILED", res?.message || "ไม่สามารถลบคลังได้");
      }
    } catch (err) {
      getAlert("FAILED", err?.message || "เกิดข้อผิดพลาดในการลบคลัง");
    } finally {
      setConfirmDialog({ isOpen: false, record: null });
    }
  };

  const filteredWarehouses = searchText
    ? warehouses.filter(
        (w) =>
          (w.WH_Code || "").toLowerCase().includes(searchText.toLowerCase()) ||
          (w.WH_Name || "").toLowerCase().includes(searchText.toLowerCase())
      )
    : warehouses;

  const columns = [
    { title: "#", key: "index", width: 80, align: "center", render: (_, __, index) => index + 1 },
    { title: "รหัสคลัง", dataIndex: "WH_Code", key: "WH_Code", width: 140 },
    { title: "ชื่อคลัง", dataIndex: "WH_Name", key: "WH_Name" },
    { title: "หมายเหตุ", dataIndex: "WH_Remark", key: "WH_Remark", ellipsis: true },
    {
      title: "จัดการ",
      key: "action",
      width: 200,
      align: "center",
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/stock/stock-overview`)}
            title="ดูสินค้า"
          />
          <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)} title="แก้ไข" />
          <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)} title="ลบ" />
        </Space>
      ),
    },
  ];

  const breadcrumbs = [
    { label: "สต๊อกสินค้า", link: "/stock/stock-overview" },
    { label: "จัดการคลังสินค้า" },
  ];

  const primaryBtnStyle = { background: "#E13F57", borderColor: "#E13F57" };

  return (
    <div className="Creditor">
      <button
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        className="btn btn-toggle-sidebar"
        style={{ position: "fixed", top: "1rem", left: isSidebarOpen ? "190px" : "-15px", zIndex: 1001, background: "transparent", border: "none", color: "#E13F57", fontSize: "20px" }}
      >
        {isSidebarOpen ? "✖" : "☰"}
      </button>
      <div className="wrapper">
        <Sidebar isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} />
        <div
          className={`main-panel ${isSidebarOpen ? "sidebar-open" : "sidebar-closed"}`}
          style={{ height: "100vh", overflow: "hidden" }}
        >
          <Topbar breadcrumbs={breadcrumbs} />
          <div className="container">
            <div className="page-inner" style={{ marginTop: "56px" }}>
              <div className="p-6" style={{ display: "flex", flexDirection: "column", minHeight: "calc(100vh - 120px)" }}>
                <div className="bg-white rounded-lg shadow-sm p-6" style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                  <div style={{ marginBottom: 16, flexShrink: 0 }}>
                    <h1 style={{ fontSize: "1.5rem", fontWeight: 600, color: "#374151" }}>จัดการคลังสินค้า</h1>
                  </div>

                  <div style={{ marginBottom: 16, flexShrink: 0, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
                    <Input
                      placeholder="ค้นหาด้วยรหัส / ชื่อคลัง..."
                      prefix={<SearchOutlined />}
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      style={{ width: 300 }}
                      allowClear
                    />
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd} style={primaryBtnStyle}>
                      เพิ่มคลังสินค้า
                    </Button>
                  </div>

                  <DataTable
                    columns={columns}
                    dataSource={filteredWarehouses}
                    loading={loading}
                    rowKey="WH_Id"
                    defaultPageSize={10}
                    pageSizeOptions={[10, 20, 50]}
                    rowClassName={(record) => {
                      if (record.WH_Id === recentlyModifiedId) return "bg-green-50";
                      if (record.WH_Id === selectedId) return "bg-blue-50";
                      return "";
                    }}
                    onRow={(record) => ({ onClick: () => setSelectedId(record.WH_Id) })}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        title={editingWarehouse ? "แก้ไขคลัง" : "เพิ่มคลัง"}
        open={modalOpen}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        maskClosable={false}
        confirmLoading={loading}
        okText={editingWarehouse ? "บันทึกการแก้ไข" : "เพิ่มคลัง"}
        cancelText="ยกเลิก"
        okButtonProps={{ disabled: !hasUnsavedChanges, style: primaryBtnStyle }}
      >
        <Form form={form} layout="vertical" onValuesChange={handleFormChange}>
          <Form.Item name="WH_Code" label="รหัสคลัง" rules={[{ required: true, message: "กรุณากรอกรหัสคลัง" }]}>
            <Input placeholder="เช่น WH001" />
          </Form.Item>
          <Form.Item name="WH_Name" label="ชื่อคลัง" rules={[{ required: true, message: "กรุณากรอกชื่อคลัง" }]}>
            <Input placeholder="ชื่อคลัง" />
          </Form.Item>
          <Form.Item name="WH_Remark" label="หมายเหตุ">
            <Input.TextArea rows={3} placeholder="รายละเอียดเพิ่มเติม (ถ้ามี)" />
          </Form.Item>
        </Form>
      </Modal>

      <ConfirmDialog
        isOpen={saveConfirmDialog}
        onClose={() => setSaveConfirmDialog(false)}
        title="ยืนยันการบันทึก"
        message={editingWarehouse ? "คุณต้องการบันทึกการแก้ไขคลังนี้หรือไม่?" : "คุณต้องการเพิ่มคลังนี้หรือไม่?"}
        confirmText="บันทึก"
        cancelText="ยกเลิก"
        onConfirm={executeSave}
      />

      <ConfirmDialog
        isOpen={closeConfirmDialog}
        onClose={() => setCloseConfirmDialog(false)}
        title="ยืนยันการปิด"
        message="คุณมีการเปลี่ยนแปลงที่ยังไม่ได้บันทึก แน่ใจหรือไม่ว่าต้องการปิดหน้าต่างนี้?"
        confirmText="ปิด"
        cancelText="ยกเลิก"
        onConfirm={executeClose}
      />

      <ErrorDialog
        isOpen={resultDialog.isOpen}
        onClose={() => setResultDialog({ ...resultDialog, isOpen: false })}
        title={resultDialog.type === "success" ? "สำเร็จ" : "เกิดข้อผิดพลาด"}
        message={resultDialog.message}
        type={resultDialog.type}
        confirmText="ตกลง"
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false, record: null })}
        title="ยืนยันการลบ"
        message={`คุณต้องการลบคลัง "${confirmDialog.record?.WH_Name}" หรือไม่?`}
        confirmText="ลบ"
        cancelText="ยกเลิก"
        onConfirm={executeDelete}
      />
    </div>
  );
}
